import React, { useRef, useEffect, useState } from "react";
import { Button } from "./ui/button";
import { UserCheck, ShieldCheck, Truck, CheckCircle } from "lucide-react";

const Hero = () => {
  const videoRef = useRef(null);
  const [videoSrc, setVideoSrc] = useState(null);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    // Detect prefers-reduced-motion — do not autoplay for users who request it
    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(motionQuery.matches);
    const onMotionChange = (e) => setPrefersReducedMotion(e.matches);
    motionQuery.addEventListener("change", onMotionChange);

    // Responsive video source selection.
    // Only the matching file is loaded; the other is never fetched.
    const mobileQuery = window.matchMedia("(max-width: 767px)");
    const selectSrc = () =>
      setVideoSrc(
        mobileQuery.matches
          ? `${process.env.PUBLIC_URL}/Hero-mobile.mp4`
          : `${process.env.PUBLIC_URL}/hero-desktop.mp4`
      );
    selectSrc();
    mobileQuery.addEventListener("change", selectSrc);

    return () => {
      motionQuery.removeEventListener("change", onMotionChange);
      mobileQuery.removeEventListener("change", selectSrc);
    };
  }, []);

  // Reload the video element when the selected source changes
  useEffect(() => {
    if (videoRef.current && videoSrc && !prefersReducedMotion) {
      videoRef.current.load();
    }
  }, [videoSrc, prefersReducedMotion]);

  return (
    <div className="relative w-full overflow-hidden bg-black flex flex-col justify-end -mt-[72px] md:-mt-[88px] min-h-[600px] md:min-h-[700px] lg:min-h-[92vh]">

      {/* Background Video — decorative only, no player UI */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 overflow-hidden">
          {!prefersReducedMotion && videoSrc && (
            <video
              ref={videoRef}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover"
            >
              <source src={videoSrc} type="video/mp4" />
            </video>
          )}
        </div>
        {/* Dark gradient overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20" />
      </div>

      {/* Hero Content */}
      <div className="relative z-10 container mx-auto px-4 pt-40 pb-28 md:pt-48 md:pb-36 flex flex-col justify-center">
        <div className="max-w-3xl text-white">
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-3 md:mb-6 leading-tight drop-shadow-md">
            Specialist Medical Cannabis Care in the UK
          </h1>
          <p className="text-base md:text-xl text-gray-200 mb-6 md:mb-10 leading-relaxed drop-shadow-sm max-w-2xl">
            Integro Clinics provides specialist assessment and clinical management for patients seeking medical cannabis treatment, where conventional approaches have not provided adequate relief.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              className="bg-[#4caf50] hover:bg-[#3d8b40] text-white text-base md:text-lg px-8 py-5 md:py-6 rounded-lg shadow-lg transition-transform hover:-translate-y-0.5"
              onClick={() => window.location.href = "https://app.integroclinics.com/v2/registration"}
            >
              Check your eligibility
            </Button>
            <Button
              variant="outline"
              className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#005eb8] text-base md:text-lg px-8 py-5 md:py-6 rounded-lg transition-all"
              onClick={() => window.location.href = "https://app.integroclinics.com/login"}
            >
              Patient Login
            </Button>
          </div>
        </div>
      </div>

      {/* Trust Bar */}
      <div className="relative z-10 w-full bg-white/95 backdrop-blur-sm py-5 border-t border-gray-100">
        <div className="container mx-auto px-4 flex flex-wrap justify-center md:justify-between items-center gap-5 text-[#012d5c]">
          <div className="flex items-center gap-2.5">
            <UserCheck className="w-5 h-5 text-[#4caf50] shrink-0" />
            <span className="font-semibold text-sm">No Consultation Charges</span>
          </div>
          <div className="flex items-center gap-2.5">
            <ShieldCheck className="w-5 h-5 text-[#4caf50] shrink-0" />
            <span className="font-semibold text-sm">GMC Registered Doctors</span>
          </div>
          <div className="flex items-center gap-2.5">
            <Truck className="w-5 h-5 text-[#4caf50] shrink-0" />
            <span className="font-semibold text-sm">Discreet Home Delivery</span>
          </div>
          <div className="flex items-center gap-2.5">
            <CheckCircle className="w-5 h-5 text-[#4caf50] shrink-0" />
            <span className="font-semibold text-sm">CQC Registered</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hero;
